import axios from 'axios';

const logQueue = []; // Queue to store log requests
let isProcessingQueue = false; // Flag to indicate if the queue is being processed

const MAX_RETRIES = 3; // Maximum number of retries for a failed log
const RETRY_DELAY = 2000; // Delay (in milliseconds) between retries

// Function to process the log queue
const processLogQueue = async () => {
  if (isProcessingQueue) return; // Prevent multiple queue processors from running
  isProcessingQueue = true;

  while (logQueue.length > 0) {
    const logItem = logQueue[0]; // Peek at the first log in the queue

    try {
      await axios.post('/api/logging', logItem.payload); // Attempt to send the log
      logQueue.shift(); // Remove the log from the queue if successful
    } catch (error) {
      logItem.retries += 1; // Increment the retry count
      if (logItem.retries >= MAX_RETRIES) {
        console.info(
          'Failed to send log after maximum retries:',
          logItem.payload
        );
        logQueue.shift(); // Remove the log from the queue after max retries
      } else {
        console.info(
          `Retrying log (${logItem.retries}/${MAX_RETRIES}) in ${RETRY_DELAY}ms`
        );
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY)); // Wait before retrying
      }
    }
  }

  isProcessingQueue = false;
};

// Function to add a log to the queue
const enqueueLog = (payload) => {
  logQueue.push({ payload, retries: 0 }); // Add the log to the queue with an initial retry count of 0
  processLogQueue(); // Start processing the queue
};

// Updated logToServer function
const logToServer = (
  level,
  message,
  context = {},
  defaultGlobalSource = '/client/global-logs'
) => {
  const source = context.source
    ? `/client${context.source}`
    : defaultGlobalSource;

  const logPayload = {
    level,
    message: typeof message === 'string' ? message : JSON.stringify(message),
    context,
    source,
    timestamp: new Date().toISOString(),
  };

  enqueueLog(logPayload); // Add the log to the queue
};

// Updated interceptConsole function
const interceptConsole = () => {
  const originalLog = console.log;
  const originalWarn = console.warn;
  const originalError = console.error;

  const defaultGlobalSource = '/client/global-logs'; // Default source for global logs

  console.log = (...args) => {
    const [message, ...context] = args;
    logToServer(
      'info',
      message,
      context.length > 0 ? context[0] : {}, // Ensure context is passed
      defaultGlobalSource
    );
    originalLog(...args); // Preserve original behavior
  };

  console.warn = (...args) => {
    const [message, ...context] = args;
    logToServer(
      'warn',
      message,
      context.length > 0 ? context[0] : {}, // Ensure context is passed
      defaultGlobalSource
    );
    originalWarn(...args); // Preserve original behavior
  };

  console.error = (...args) => {
    const [message, ...context] = args;
    logToServer(
      'error',
      message,
      context.length > 0 ? context[0] : {}, // Ensure context is passed
      defaultGlobalSource
    );
    originalError(...args); // Preserve original behavior
  };

  // Intercept uncaught JavaScript errors
  window.onerror = (message, source, lineno, colno, error) => {
    logToServer('error', message, {
      source: '/global-errors',
      errorSource: source,
      lineno,
      colno,
      stack: error?.stack,
    });
  };

  // Intercept unhandled promise rejections
  window.onunhandledrejection = (event) => {
    logToServer('error', 'Unhandled rejection', {
      reason: event.reason
        ? typeof event.reason === 'object'
          ? event.reason
          : JSON.stringify(event.reason, null, 2)
        : 'Unknown reason',
      source: '/unhandled-rejections',
    });
  };
};

export default interceptConsole;
